/**
 * Utilidades para detectar el tipo de dispositivo y características del hardware
 */
import { useState, useEffect } from 'react';

/**
 * Detecta si el dispositivo es una Raspberry Pi
 * Basado en User-Agent y características del navegador
 */
export const isRaspberryPi = (): boolean => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }

  const userAgent = navigator.userAgent.toLowerCase();
  const platform = navigator.platform?.toLowerCase() || '';

  // Detectar Raspberry Pi por User-Agent común
  // Chromium en Raspberry Pi suele incluir "arm" o "raspberry" en el User-Agent
  if (
    userAgent.includes('raspberry') ||
    userAgent.includes('rpi') ||
    (userAgent.includes('arm') && userAgent.includes('linux')) ||
    platform.includes('linux arm')
  ) {
    return true;
  }

  // Detectar por características del hardware
  // Raspberry Pi suele tener limitaciones de hardware que podemos detectar
  const hardwareConcurrency = navigator.hardwareConcurrency || 0;
  const deviceMemory = (navigator as any).deviceMemory || 0;

  // Raspberry Pi 4 tiene 4 cores, pero puede tener menos memoria disponible
  // Si tiene 4 o menos cores y poca memoria, podría ser Raspberry Pi
  if (hardwareConcurrency <= 4 && deviceMemory <= 4 && platform.includes('linux')) {
    // Verificar si es ARM (Raspberry Pi usa ARM)
    if (userAgent.includes('arm') || platform.includes('arm')) {
      return true;
    }
  }

  return false;
};

/**
 * Detecta si el dispositivo tiene capacidades limitadas de hardware
 * Útil para aplicar optimizaciones adicionales
 */
export const hasLimitedHardware = (): boolean => {
  if (typeof navigator === 'undefined') {
    return false;
  }

  const hardwareConcurrency = navigator.hardwareConcurrency || 0;
  const deviceMemory = (navigator as any).deviceMemory || 0;

  // Considerar hardware limitado si tiene 4 o menos cores o 4GB o menos de RAM
  return hardwareConcurrency <= 4 || deviceMemory <= 4;
};

/**
 * Hook de React para detectar si es Raspberry Pi
 * Devuelve el estado de detección y si se está cargando
 */
export interface DeviceInfo {
  isRaspberryPi: boolean;
  hasLimitedHardware: boolean;
  isLoading: boolean;
}

export const useDeviceDetection = (): DeviceInfo => {
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>({
    isRaspberryPi: false,
    hasLimitedHardware: false,
    isLoading: true,
  });

  useEffect(() => {
    // Ejecutar detección solo en el cliente
    if (typeof window !== 'undefined') {
      const isRPi = isRaspberryPi();
      const hasLimited = hasLimitedHardware();

      setDeviceInfo({
        isRaspberryPi: isRPi,
        hasLimitedHardware: hasLimited,
        isLoading: false,
      });
    } else {
      setDeviceInfo({
        isRaspberryPi: false,
        hasLimitedHardware: false,
        isLoading: false,
      });
    }
  }, []);

  return deviceInfo;
};

